import axios, { AxiosError } from 'axios';

const knowledgeBaseApi = axios.create({
	baseURL: (
		process.env.REACT_APP_KB_API_PROXY_PATH ||
		process.env.REACT_APP_KB_API_URL ||
		'http://159.65.149.168:8004'
	).replace(/\/$/, ''),
	timeout: 600000,
});

export type PipelineStatus =
	| 'idle'
	| 'running'
	| 'completed'
	| 'failed'
	| string;

export interface ServiceHealth {
	status: string;
	service: string;
	version: string;
	timestamp: number;
}

export interface PipelineProgress {
	pipeline: string | null;
	status: PipelineStatus;
	run_id: string | null;
	started_at: string | null;
	finished_at: string | null;
	current_step: string | null;
	step_index: number;
	step_total: number;
	item_label: string | null;
	item_current: number;
	item_total: number;
	stage_index: number;
	stage_total: number;
	stage_name: string | null;
	cancelling: boolean;
	result: Record<string, unknown> | null;
	error: string | null;
}

export interface UrlStatusItem {
	url: string;
	status: 'missing' | 'excess' | 'pending' | 'scraped' | string;
}

export interface UrlStatusResponse {
	success: boolean;
	message: string;
	sitemap_url: string;
	database: string;
	data: UrlStatusItem[];
	summary: Record<string, number>;
}

export interface OperationResponse {
	status: string;
	message?: string | null;
	error?: string | null;
	[key: string]: unknown;
}

export interface KnowledgeDocument {
	name: string;
	filename?: string;
	chunk_count?: number;
	chunks?: number;
	has_file?: boolean;
	size?: number;
	created_at?: string;
	updated_at?: string;
	[key: string]: unknown;
}

export interface DocumentsResponse {
	documents: KnowledgeDocument[];
}

export interface KnowledgeChunk {
	id?: number | string | null;
	context_id?: number | string;
	title?: string;
	context?: string;
	page_content?: string;
	brief_summary?: string;
	keywords?: string;
	context_type?: string;
	sub_type?: string;
	source_url?: string;
	status?: string;
	metadata?: Record<string, unknown>;
	editable?: Record<string, unknown> | null;
	category_label?: string | null;
	is_official?: boolean | null;
	[key: string]: unknown;
}

export interface CheckUrlsResponse {
	matched_urls: string[];
	results: Record<string, KnowledgeChunk[]>;
	not_found: string[];
	error?: string | null;
}

export interface RetrievalDocument extends KnowledgeChunk {
	id?: string | null;
	page_content: string;
	source: string;
}

export interface RetrievalTestResponse {
	status: string;
	question: string;
	rewritten_question: string;
	categories: string[];
	subcategories: string[];
	metadata_filter: Record<string, unknown>;
	is_relevant?: string | null;
	unfiltered_count: number;
	filtered_count: number;
	total_count: number;
	documents: RetrievalDocument[];
	final_count: number;
	final_order: Array<string | number>;
	error?: string | null;
}

export interface SourceUrlOption {
	url: string;
	category_label: string;
	is_official: boolean;
	is_current: boolean;
	is_linked: boolean;
}

export interface SourceUrlOptionsResponse extends OperationResponse {
	context_id: number;
	linked_urls: string[];
	source_url: string;
	override: string | null;
	is_overridden: boolean;
	options: SourceUrlOption[];
}

function getErrorMessage(error: unknown, fallback: string) {
	if (!axios.isAxiosError(error)) return fallback;

	const axiosError = error as AxiosError<{
		detail?: string | Array<{ msg?: string }>;
		message?: string;
		error?: string;
	}>;
	const data = axiosError.response?.data;
	const details = data?.detail;
	if (typeof details === 'string') return details;
	if (Array.isArray(details)) {
		return details
			.map(item => item.msg)
			.filter(Boolean)
			.join(', ');
	}
	if (data?.message) return data.message;
	if (data?.error) return data.error;
	if (axiosError.code === 'ECONNABORTED') {
		return 'The knowledge base service took too long to respond.';
	}
	if (!axiosError.response) {
		return 'Could not reach the knowledge base service.';
	}
	return fallback;
}

async function request<T>(work: Promise<{ data: T }>, fallback: string) {
	try {
		return (await work).data;
	} catch (error) {
		throw new Error(getErrorMessage(error, fallback));
	}
}

export function getKnowledgeBaseHealth() {
	return request(
		knowledgeBaseApi.get<ServiceHealth>('/status/health'),
		'Could not check service health.',
	);
}

export function getPipelineProgress() {
	return request(
		knowledgeBaseApi.get<PipelineProgress>(
			'/kb_management/pipeline-progress',
		),
		'Could not load pipeline progress.',
	);
}

export function getDocumentProgress() {
	return request(
		knowledgeBaseApi.get<PipelineProgress>(
			'/kb_management/documents/progress',
		),
		'Could not load document progress.',
	);
}

export function getUrlStatus(sitemapUrl: string) {
	return request(
		knowledgeBaseApi.get<UrlStatusResponse>(
			`/kb_management/url-status?sitemap_url=${encodeURIComponent(sitemapUrl)}&db_name=sqlite.db`,
		),
		'Could not compare the sitemap with the knowledge base.',
	);
}

export function syncFromSitemap(sitemapUrl: string, password: string) {
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/sync-from-sitemap',
			{ sitemap_url: sitemapUrl, password },
		),
		'Could not start the sitemap sync.',
	);
}

export function updateFromUrls(urls: string[]) {
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/update-from-urls',
			{ urls },
		),
		'Could not update the selected URLs.',
	);
}

export function deleteUrls(urls: string[]) {
	return request(
		knowledgeBaseApi.delete<OperationResponse>(
			'/kb_management/delete_urls',
			{
				data: { urls },
			},
		),
		'Could not delete the selected URLs.',
	);
}

export function checkUrls(urls: string[]) {
	return request(
		knowledgeBaseApi.post<CheckUrlsResponse>('/kb_management/check-urls', {
			urls,
		}),
		'Could not load URL content.',
	);
}

export function editContext(
	contextId: number,
	updatedData: Record<string, unknown>,
	keepEmbedding = false,
) {
	const route = keepEmbedding
		? '/kb_management/update-context-text'
		: '/kb_management/edit-context';
	return request(
		knowledgeBaseApi.post<OperationResponse>(route, {
			context_id: contextId,
			updated_data: updatedData,
		}),
		'Could not update the content chunk.',
	);
}

export function deleteContext(contextId: number) {
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/delete-context',
			{ context_id: contextId },
		),
		'Could not delete the content chunk.',
	);
}

export function getSourceUrlOptions(contextId: number) {
	return request(
		knowledgeBaseApi.get<SourceUrlOptionsResponse>(
			`/kb_management/source-url-options/${contextId}`,
		),
		'Could not load source URL options.',
	);
}

export function setSourceUrl(
	contextId: number,
	sourceUrl: string,
	reembed = false,
) {
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/set-source-url',
			{
				context_id: contextId,
				source_url: sourceUrl,
				reembed,
			},
		),
		'Could not change the source URL.',
	);
}

export function retrieveTest(question: string) {
	return request(
		knowledgeBaseApi.post<RetrievalTestResponse>(
			'/kb_management/retrieve-test',
			{ question },
		),
		'Could not test knowledge retrieval.',
	);
}

export function getContextById(contextId: string) {
	return request(
		knowledgeBaseApi.post<{
			status: string;
			context_id: string;
			found: boolean;
			data?: KnowledgeChunk;
			error?: string | null;
		}>('/kb_management/context-by-id', { context_id: contextId }),
		'Could not find that context ID.',
	);
}

export function getDocuments() {
	return request(
		knowledgeBaseApi.get<DocumentsResponse>('/kb_management/documents'),
		'Could not load documents.',
	);
}

export function uploadDocuments(files: File[], ocrPptxImages: boolean) {
	const form = new FormData();
	files.forEach(file => form.append('files', file, file.name));
	form.append('ocr_pptx_images', String(ocrPptxImages));
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/documents/upload',
			form,
		),
		'Could not upload the documents.',
	);
}

export function cancelDocumentUpload() {
	return request(
		knowledgeBaseApi.post<OperationResponse>(
			'/kb_management/documents/cancel',
		),
		'Could not cancel the document upload.',
	);
}

export function getDocumentChunks(name: string) {
	return request(
		knowledgeBaseApi.get<Record<string, unknown>>(
			`/kb_management/documents/${encodeURIComponent(name)}/chunks`,
		),
		'Could not load document chunks.',
	);
}

export function getDocumentFile(name: string) {
	return request(
		knowledgeBaseApi.get<Blob>(
			`/kb_management/documents/${encodeURIComponent(name)}/file`,
			{ responseType: 'blob' },
		),
		'Could not download the document.',
	);
}

export function deleteDocument(name: string) {
	return request(
		knowledgeBaseApi.delete<OperationResponse>(
			`/kb_management/documents/${encodeURIComponent(name)}`,
		),
		'Could not delete the document.',
	);
}
